import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllUsers } from "../../../../data/getters";
import authApi from "../../../../data/api/authApi";
import { useLanguage } from "../../../helpers/LanguageContext";

const EMPTY_FORM = {
  name: "",
  userName: "",
  email: "",
  address: "",
  role: "companyUser"
};

export default function useEmployeePageController() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const role = useSelector((state) => state.auth.user?.role);
  const canAddEmployee = ["admin", "companyAdmin"].includes(role);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [createForm, setCreateForm] = useState(EMPTY_FORM);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const allUsers = await dispatch(getAllUsers());
      setEmployees(
        allUsers.filter(
          (user) =>
            user.role === "companyUser" ||
            (canAddEmployee && user.role === "companyAdmin")
        )
      );
    } catch (error) {
      console.error("Failed to load employees", error);
    } finally {
      setLoading(false);
    }
  }, [canAddEmployee, dispatch]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const translateRole = useCallback(
    (employeeRole) => {
      if (!employeeRole) return t("none");
      return t(employeeRole.toLowerCase()) ?? employeeRole;
    },
    [t]
  );

  const openCreate = useCallback(() => {
    setCreateErr("");
    setShowCreate(true);
  }, []);

  const closeCreate = useCallback(() => {
    setShowCreate(false);
  }, []);

  const handleCreateChange = useCallback((event) => {
    const { name, value } = event.target;
    setCreateForm((form) => ({ ...form, [name]: value }));
  }, []);

  const handleCreateEmployee = useCallback(
    async (event) => {
      event.preventDefault();
      setCreating(true);
      setCreateErr("");

      try {
        const response = await authApi.registerApi({
          ...createForm,
          password: "Welcome1"
        });
        const { success, message } = response.data;

        if (!success) {
          setCreateErr(message || t("createEmployeeError"));
          return;
        }

        setShowCreate(false);
        setCreateForm(EMPTY_FORM);
        await loadEmployees();
      } catch (error) {
        setCreateErr(
          error.response?.data?.message ||
            JSON.stringify(error.response?.data) ||
            t("createEmployeeError")
        );
      } finally {
        setCreating(false);
      }
    },
    [createForm, loadEmployees, t]
  );

  const handleEmployeeClick = useCallback(
    (employeeId) => {
      navigate(`/userAnalyzer/${employeeId}`);
    },
    [navigate]
  );

  return {
    t,
    employees,
    loading,
    canAddEmployee,
    showCreate,
    creating,
    createErr,
    createForm,
    translateRole,
    openCreate,
    closeCreate,
    handleCreateChange,
    handleCreateEmployee,
    handleEmployeeClick
  };
}
